import { FC, useMemo } from 'react';
import { format } from 'date-fns';
import { useTranslate } from 'hooks/useTranslate';
import { formatCurrency, formatNumber } from 'utils/formats';
import { dateFormat } from 'constants/formats';
import {
  IncentiveOffer,
  IncentiveOfferPlanClass,
  IncentiveOfferType,
  IncentivesMarketBeneficiary,
} from 'modules/incentivesMarket/types';

import { isPlanClassVisible } from 'modules/incentivesMarket/utils/isPlanClassVisible';
import incentiveOfferStatus from '../../../constants/incentiveOfferStatus';
import IncentiveOfferPartialityIcon from './IncentiveOfferPartialityIcon';
import IncentiveOfferStatusBadge, {
  IncentiveOfferStatusBadgeVariant,
} from '../../../components/IncentiveOfferStatusBadge';
import { getOfferStatus } from '../../../utils/getOfferStatus';

type IncentiveOfferRowProps = {
  offer: IncentiveOffer;
  onViewDetail: (offerId: string) => void; // eslint-disable-line no-unused-vars
};

const rowBorderColor = {
  [IncentiveOfferType.SELL]: 'border-left-2 border-left-info',
  [IncentiveOfferType.BUY]: 'border-left-2 border-left-primary',
};

const IncentiveOfferRow: FC<IncentiveOfferRowProps> = ({ offer, onViewDetail }) => {
  const { t } = useTranslate();

  const isSellOffer = offer.type === IncentiveOfferType.SELL;

  const partnerName = useMemo(() => {
    if (offer.isSellerNameHidden && offer.type === IncentiveOfferType.SELL)
      return t('HiddenName');
    if (typeof offer.beneficiary === 'string') {
      return '';
    }
    return (offer.beneficiary as IncentivesMarketBeneficiary)?.name || '';
  }, [offer.beneficiary, offer.isSellerNameHidden, offer.type, t]);

  const unitClassName = useMemo(() => {
    if (typeof offer.unitClass === 'string') {
      return '';
    }
    return (offer.unitClass as IncentiveOfferPlanClass)?.name || '';
  }, [offer.unitClass]);

  const totalAmount = offer.unitsCount.total * offer.unitPrice;

  const progressPercent = useMemo(() => {
    if (offer.unitsCount.total === 0) return 0;
    return Math.round(
      (offer.unitsCount.agreed / offer.unitsCount.total) * 100
    );
  }, [offer.unitsCount]);

  const status = getOfferStatus(offer);
  const statusConfig = incentiveOfferStatus[status];

  const isPartialOffer = Boolean(offer?.isPartialAmountEnabled);

  const handleClick = () => {
    onViewDetail(offer._id);
  };

  return (
    <div
      className={`nk-iv-scheme-item bg-white border cursor-pointer hover-shadow hover-scale ${
        rowBorderColor[offer.type]
      }`}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyPress={(e) => e.key === 'Enter' && handleClick()}
    >
      <div
        className={`nk-iv-scheme-icon bg-lighter ${
          status === 'ACTIVE' ? 'is-running' : ''
        }`}
        title={isSellOffer ? t('Sale') : t('Purchase')}
      >
        <em
          className={`icon ni ${
            isSellOffer ? 'ni-wallet-out' : 'ni-wallet-in'
          }`}
        />
      </div>

      <div className="nk-iv-scheme-info nk-iv-scheme-info-stack text-start">
        <div className="nk-iv-scheme-block-left">
          <div className="nk-iv-scheme-name text-dark">
            {formatNumber(offer.unitsCount.total)} {t('IncentivesMarketUnits').toLowerCase()}
            <IncentiveOfferPartialityIcon
              id={offer._id}
              isPartial={isPartialOffer}
            />
          </div>

          {isPlanClassVisible() && (
            <div className="nk-iv-scheme-desc text-soft">
              {t('Class')}:&nbsp;
              <span className="text-dark">{unitClassName || '-'}</span>
            </div>
          )}
        </div>

        <div className="nk-iv-scheme-block-right">
          <div className="nk-iv-scheme-desc">
            {t('IncentivesMarketPricePerUnit')}:&nbsp;
            <span className="amount text-dark">
              {formatCurrency(offer.unitPrice)}
            </span>
          </div>
          <div className="nk-iv-scheme-desc text-soft">
            {t('Total')}:&nbsp;
            <span className="amount text-dark">
              {formatCurrency(totalAmount)}
            </span>
          </div>
        </div>
      </div>

      <div className="nk-iv-scheme-term">
        <div className="nk-iv-scheme-start nk-iv-scheme-order">
          <span className="nk-iv-scheme-label text-soft">{t('Start')}</span>
          <span className="nk-iv-scheme-value date text-dark">
            {format(new Date(offer.startDate), dateFormat)}
          </span>
        </div>
        <div className="nk-iv-scheme-end nk-iv-scheme-order">
          <span className="nk-iv-scheme-label text-soft">{t('End')}</span>
          <span className="nk-iv-scheme-value date text-dark">
            {format(new Date(offer.endDate), dateFormat)}
          </span>
        </div>
      </div>

      <div className="nk-iv-scheme-amount">
        <div className="nk-iv-scheme-amount-a nk-iv-scheme-order">
          <span className="nk-iv-scheme-label text-soft">
            {isSellOffer ? t('Seller') : t('Buyer')}
          </span>
          <span className="nk-iv-scheme-value text-dark">{partnerName}</span>
        </div>
        <div className="nk-iv-scheme-amount-b nk-iv-scheme-order">
          <span className="nk-iv-scheme-label text-soft">{t('Status')}</span>
          <IncentiveOfferStatusBadge
            status={status}
            variant={IncentiveOfferStatusBadgeVariant.TEXT}
          />
        </div>
      </div>

      <div className="nk-iv-scheme-more">
        <button
          type="button"
          className="btn btn-icon btn-lg btn-round btn-trans"
          title={t('ViewDetail')}
          onClick={(e) => {
            e.stopPropagation();
            handleClick();
          }}
        >
          <em className="icon ni ni-forward-ios" />
        </button>
      </div>

      <div className="nk-iv-scheme-progress">
        <div
          className={`progress-bar ${statusConfig.fill}`}
          style={{ width: `${progressPercent}%` }}
        />
      </div>
    </div>
  );
};

export default IncentiveOfferRow;
